import { MapStyleSchema } from './map-style-contract.js';
import { CameraPlatformSchema } from './camera-platform-contract.js';
import { CameraGimbalSchema } from './camera-gimbal-contract.js';
import { z } from 'zod';
import { CameraAngleSchema } from './camera-angle-contract.js';
import { DepthOfFieldSchema } from './depth-of-field-contract.js';
import { CAMERA_DISTANCE_IDS } from './presets.js';
import { MAX_CINEMATIC_WAYPOINTS } from './cinematic-sequence.js';
import { CameraKeyframesSchema } from './camera-keyframes-contract.js';
import { resolveCameraKeyframes } from './camera-keyframes.js';
import type { CinematicSequence } from './cinematic-sequence.js';
import { PresentationAppearanceSchema } from './presentation-contract.js';

export const CinematicWaypointSchema = z.object({
  id: z.string().min(1).max(64),
  filmProgress: z.number().min(0).max(1).optional(),
  anchor: z.object({ pointIndex: z.int().min(0).max(99_999), recordedAt: z.iso.datetime({ offset: true }) }).strict(),
  overrides: z.object({
    cameraGimbal: CameraGimbalSchema.optional(),
    cameraStyle: CameraPlatformSchema.optional(),
    mapStyle: MapStyleSchema.optional(), cameraAngle: CameraAngleSchema.nullable().optional(),
    cameraDistance: z.enum(CAMERA_DISTANCE_IDS).optional(), depthOfField: DepthOfFieldSchema.optional(),
    cameraZoom: z.number().min(2).max(20).nullable().optional(),
    presentationAppearance: PresentationAppearanceSchema.optional(),
  }).strict(),
  transition: z.discriminatedUnion('type', [z.object({ type: z.literal('cut'), timing: z.literal('arrival').optional() }).strict(),
    z.object({ type: z.literal('smooth'), durationSeconds: z.number().min(.1).max(30), timing: z.literal('arrival').optional(), entireInterval: z.boolean().optional(), easing: z.enum(['linear', 'smooth']).optional() }).strict()]),
}).strict();

export const CinematicSequenceSchema = z.object({
  version: z.literal(1), waypoints: z.array(CinematicWaypointSchema).max(MAX_CINEMATIC_WAYPOINTS),
  cameraTracks: CameraKeyframesSchema.optional(),
}).strict().superRefine((sequence, context) => {
  if (new TextEncoder().encode(JSON.stringify(sequence)).length > 24 * 1024) context.addIssue({ code: 'custom', message: 'The sequence is too large. Remove some keys before exporting.' });
  const identities = new Set<string>();
  const positions = new Set<string>();
  for (const [index, waypoint] of sequence.waypoints.entries()) {
    const position = waypoint.filmProgress === undefined ? `sample:${waypoint.anchor.pointIndex}` : `film:${waypoint.filmProgress}`;
    const properties = Object.keys(waypoint.overrides);
    const occupied = (properties.length ? properties : ['scene']).map(property => `${position}:${property}`);
    if (identities.has(waypoint.id) || occupied.some(key => positions.has(key))) {
      context.addIssue({ code: 'custom', message: 'Cinematic keyframes must have unique identities and positions for each property.', path: ['waypoints', index] });
    }
    identities.add(waypoint.id); occupied.forEach(key => positions.add(key));
  }
});

/** Stored invalid versions must never silently become a different film. */
export function readStoredCinematicSequence(value: string | null | undefined) {
  return value == null ? undefined : CinematicSequenceSchema.parse(JSON.parse(value));
}

/** Validate film-time tracks at the render boundary, where the full duration is known. */
export function validCameraTrackTiming(value: { durationSeconds: number; cinematicSequence?: CinematicSequence | undefined }): boolean {
  try {
    if (value.cinematicSequence?.cameraTracks) resolveCameraKeyframes(value.cinematicSequence.cameraTracks, value.durationSeconds);
    return true;
  } catch { return false; }
}
export const CAMERA_TRACK_TIMING_ERROR = 'Camera keys must be within the film duration.';
